import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FiDownload,
  FiMapPin,
  FiCalendar,
  FiMail,
  FiPhone,
  FiLinkedin,
  FiGithub,
  FiTwitter,
  FiInstagram,
  FiYoutube,
  FiArrowUpRight,
} from "react-icons/fi";
import { profileAPI, getImageUrl } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";

/* ─────────────────────────────────────────────
   Social platform → icon map
   ───────────────────────────────────────────── */
const socialIconMap = {
  github: FiGithub,
  linkedin: FiLinkedin,
  email: FiMail,
  phone: FiPhone,
  twitter: FiTwitter,
  x: FiTwitter,
  instagram: FiInstagram,
  youtube: FiYoutube,
};

/* ─────────────────────────────────────────────
   SECTION HEADER — numbered, editorial style
   ───────────────────────────────────────────── */
const SectionHeader = ({ number, eyebrow, title, description, inView }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={inView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.55 }}
    className="mb-12"
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="font-mono text-xs tracking-[0.22em] text-blue-600 dark:text-blue-400">
        {number}
      </span>
      <span className="h-px w-10 bg-blue-600/40 dark:bg-blue-400/40" />
      <span className="font-mono text-xs tracking-[0.22em] uppercase text-slate-500 dark:text-slate-400">
        {eyebrow}
      </span>
    </div>
    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
      {title}
    </h2>
    {description && (
      <p className="mt-3 max-w-2xl text-slate-600 dark:text-slate-400 leading-relaxed">
        {description}
      </p>
    )}
  </motion.div>
);

/* ─────────────────────────────────────────────
   TIMELINE ITEM — used for Experience & Education
   Props:
     • title       – job position / degree title
     • subtitle    – company / institution name
     • duration    – "Jan 2022 – Present"
     • description – body text
     • logo        – image URL (optional). If provided → shows logo.
                     If null/undefined → shows a fallback initial badge.
     • index       – for staggered animation delay
     • inView      – from useInView hook
     • isLast      – hides connector line on the last item
   ───────────────────────────────────────────── */
/** Logo tile: 48px mobile, 56px sm+ — full-bleed image, no frame border */
const logoBoxClassName =
  "relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 sm:h-14 sm:w-14 sm:rounded-xl dark:bg-slate-800";

const TimelineItem = ({
  title,
  subtitle,
  duration,
  description,
  logo,
  index,
  inView,
  isLast,
}) => {
  const initials =
    subtitle && subtitle.trim().length >= 2
      ? `${subtitle.trim().charAt(0)}${
          subtitle.trim().split(/\s+/)[1]?.charAt(0) ||
          subtitle.trim().charAt(1) ||
          ""
        }`.toUpperCase()
      : subtitle
        ? subtitle.trim().charAt(0).toUpperCase()
        : "•";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.48, delay: index * 0.07, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative flex gap-0 pb-8 last:pb-0 sm:gap-4 sm:pb-10 md:gap-5"
    >
      {/* Timeline rail — sm+ only (cleaner on phone / small tablet portrait) */}
      <div
        className="relative hidden w-10 flex-shrink-0 flex-col items-center sm:flex"
        aria-hidden
      >
        <div className="flex w-full justify-center pt-5 md:pt-6">
          <div className="flex h-14 w-full items-center justify-center">
            <span className="h-2 w-2 rounded-full border-2 border-white bg-slate-800 shadow-sm dark:border-slate-950 dark:bg-white" />
          </div>
        </div>
        {!isLast && (
          <span className="absolute left-1/2 top-[calc(1.25rem+3.5rem)] bottom-[-2.5rem] w-px -translate-x-1/2 bg-slate-200 dark:bg-slate-700 md:top-[calc(1.5rem+3.5rem)] md:bottom-[-2.75rem]" />
        )}
      </div>

      {/* Card — mobile: full width + left accent; sm+: rail handles timeline */}
      <div className="min-w-0 flex-1">
        <div
          className="group relative overflow-hidden rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm transition-shadow duration-300 hover:border-slate-300 hover:shadow-md sm:rounded-2xl sm:border-l sm:p-5 sm:pl-5 md:p-6 dark:border-slate-800 dark:bg-slate-900/50 dark:hover:border-slate-700"
        >
          {/* Mobile / small tablet: professional left accent (replaces rail) */}
          <div
            className="pointer-events-none absolute left-0 top-4 bottom-4 w-1 rounded-full bg-gradient-to-b from-blue-600 to-indigo-600 opacity-[0.92] sm:hidden"
            aria-hidden
          />

          <div className="relative pl-3.5 sm:pl-0">
            {/* Header row: logo + title block — always horizontal (standard list layout) */}
            <div className="flex gap-3.5 sm:gap-5 md:gap-6">
              <div className={logoBoxClassName}>
                {logo ? (
                  <img
                    src={logo}
                    alt={subtitle ? `${subtitle} logo` : ""}
                    width={56}
                    height={56}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span
                    className="text-xs font-semibold tabular-nums tracking-tight text-slate-700 sm:text-sm dark:text-slate-200"
                    aria-hidden
                  >
                    {initials}
                  </span>
                )}
              </div>

              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-start min-[480px]:justify-between min-[480px]:gap-3 sm:gap-4">
                  <div className="min-w-0 space-y-0.5 sm:space-y-1">
                    <h3 className="text-base font-semibold leading-snug tracking-tight text-slate-900 sm:text-lg md:text-xl dark:text-white">
                      {title}
                    </h3>
                    {subtitle && (
                      <p className="text-[13px] font-medium leading-snug text-slate-600 sm:text-sm dark:text-slate-400">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  {duration && (
                    <span className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[9px] font-semibold uppercase leading-tight tracking-wide text-slate-600 sm:text-[10px] sm:px-2.5 sm:py-1 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
                      <FiCalendar className="shrink-0 text-slate-400 dark:text-slate-500" size={11} strokeWidth={2} />
                      <span className="break-words">{duration}</span>
                    </span>
                  )}
                </div>

                {description && (
                  <p className="border-t border-slate-100 pt-3 text-[13px] leading-relaxed text-slate-600 sm:text-sm dark:border-slate-800/90 dark:text-slate-400 md:text-[0.9375rem]">
                    {description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   MAIN PAGE
   ───────────────────────────────────────────── */
const About = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [aboutRef, aboutInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [experienceRef, experienceInView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [educationRef, educationInView] = useInView({ threshold: 0.05, triggerOnce: true });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileAPI.getProfile();
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <LoadingSpinner size="lg" text="Loading profile..." />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            Profile not found
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Please check back later or contact the administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200">

      {/* ══════════════════════════════════════
          01 — HERO / ABOUT
          ══════════════════════════════════════ */}
      <section ref={aboutRef} className="relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 text-slate-900 dark:text-white opacity-[0.04] dark:opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse at center, black 60%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 60%, transparent 100%)",
          }}
        />
        {/* Soft ambient glow */}
        <div className="absolute -top-32 right-1/4 w-[26rem] h-[26rem] bg-blue-200/40 dark:bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-20 w-[18rem] h-[18rem] bg-indigo-200/30 dark:bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5 lg:px-12 py-16 lg:py-24">

          {/* Page eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={aboutInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-3 mb-10"
          >
            <span className="font-mono text-xs tracking-[0.22em] text-blue-600 dark:text-blue-400">
              01
            </span>
            <span className="h-px w-10 bg-blue-600/40 dark:bg-blue-400/40" />
            <span className="font-mono text-xs tracking-[0.22em] uppercase text-slate-500 dark:text-slate-400">
              About
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

            {/* ── Profile Image (5 cols) ── */}
            {profile.avatar && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={aboutInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-5"
              >
                <div className="relative group max-w-sm mx-auto lg:mx-0">
                  {/* Backing frame card */}
                  <div className="absolute -inset-3 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm" />

                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.45)]">
                    <img
                      src={getImageUrl(profile.avatar.url)}
                      alt={profile.name}
                      className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    {/* Editorial vignette */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                  </div>

                  {/* Floating availability badge */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:-right-4 flex items-center gap-2 rounded-full bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 shadow-lg px-4 py-2 whitespace-nowrap">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                      Available for work
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Info (7 cols) ── */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-7 space-y-7"
            >
              <div>
                <h1 className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold tracking-tight text-slate-900 dark:text-white leading-[1.05]">
                  {profile.name}
                </h1>
                <p className="mt-3 text-lg md:text-xl text-slate-600 dark:text-slate-300 font-medium">
                  {profile.title}
                </p>
                {profile.location && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                    <FiMapPin size={14} /> {profile.location}
                  </p>
                )}
              </div>

              {/* Editorial pull-quote bio */}
              <div className="relative pl-5 border-l-2 border-blue-500/60 dark:border-blue-400/60">
                <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
                  {profile.bio}
                </p>
              </div>

              {/* Stats meta row */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl md:text-[1.7rem] font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                    {profile.experience?.length || 0}
                  </span>
                  <span className="text-xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">
                    Roles
                  </span>
                </div>
                <span className="hidden sm:inline-block w-px h-7 bg-slate-200 dark:bg-slate-700" />
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl md:text-[1.7rem] font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                    {profile.education?.length || 0}
                  </span>
                  <span className="text-xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">
                    Degrees
                  </span>
                </div>
                <span className="hidden sm:inline-block w-px h-7 bg-slate-200 dark:bg-slate-700" />
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl md:text-[1.7rem] font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                    24<span className="text-blue-600 dark:text-blue-400">/7</span>
                  </span>
                  <span className="text-xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">
                    Reachable
                  </span>
                </div>
              </div>

              {/* CTA + socials */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {profile.resume && (
                  <a
                    href={getImageUrl(profile.resume.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 rounded-full text-sm font-semibold shadow-[0_8px_24px_-8px_rgba(15,23,42,0.45)] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <FiDownload size={15} />
                    Download Resume
                    <FiArrowUpRight
                      size={14}
                      className="opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                )}

                {profile.social &&
                  Object.entries(profile.social).map(([platform, url]) => {
                    if (!url) return null;
                    const key = platform.toLowerCase();

                    // ── Hide phone from public social row ──
                    // Phone number is intentionally not shown on the public
                    // About page. Remove / comment out the next line to
                    // show the phone icon (it will become a `tel:` link).
                    if (key === "phone") return null;

                    const Icon = socialIconMap[key];

                    let href = url;
                    if (key === "email") href = `mailto:${url}`;
                    else if (key === "phone") href = `tel:${String(url).replace(/\s+/g, "")}`;

                    const isInternal = key === "email" || key === "phone";

                    return (
                      <a
                        key={platform}
                        href={href}
                        target={isInternal ? "_self" : "_blank"}
                        rel="noopener noreferrer"
                        title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                        aria-label={platform}
                        className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-800 hover:ring-blue-500 dark:hover:ring-blue-400 hover:text-blue-600 dark:hover:text-blue-400 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        {Icon ? <Icon size={16} /> : (
                          <span className="text-xs font-medium capitalize">
                            {platform.charAt(0)}
                          </span>
                        )}
                      </a>
                    );
                  })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          02 — EXPERIENCE
          ══════════════════════════════════════ */}
      {profile?.experience && profile.experience.length > 0 && (
        <section
          ref={experienceRef}
          className="relative bg-white dark:bg-slate-900/40 border-y border-slate-100 dark:border-slate-800/80"
        >
          <div className="max-w-4xl mx-auto px-5 lg:px-12 py-20">
            <SectionHeader
              number="02"
              eyebrow="Career"
              title="Professional Experience"
              description="Roles I've held and the work I shipped along the way."
              inView={experienceInView}
            />
            <div>
              {profile.experience.map((exp, index) => (
                <TimelineItem
                  key={index}
                  index={index}
                  inView={experienceInView}
                  isLast={index === profile.experience.length - 1}
                  title={exp.position}
                  subtitle={exp.company}
                  duration={exp.duration}
                  description={exp.description}
                  /*
                   * ── LOGO GUIDE ──────────────────────────────────────
                   * Pass `exp.logo` (a URL string) from your API/CMS.
                   *
                   * ✅ Logo present → shows the company logo image
                   * ❌ Logo absent  → shows a styled initial badge
                   *    (first letter of the company name)
                   *
                   * In your Strapi / API response, add a `logo` field to
                   * each experience entry and upload the company logo there.
                   * Example shape:
                   *   { position, company, duration, description, logo: { url: "/uploads/google.png" } }
                   * ────────────────────────────────────────────────────
                   */
                  logo={exp.logo ? getImageUrl(exp.logo.url) : null}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          03 — EDUCATION
          ══════════════════════════════════════ */}
      {profile?.education && profile.education.length > 0 && (
        <section ref={educationRef} className="bg-slate-50 dark:bg-slate-950">
          <div className="max-w-4xl mx-auto px-5 lg:px-12 py-20">
            <SectionHeader
              number="03"
              eyebrow="Academia"
              title="Education"
              description="Degrees, certifications, and the institutions that shaped my path."
              inView={educationInView}
            />
            <div>
              {profile.education.map((edu, index) => (
                <TimelineItem
                  key={index}
                  index={index}
                  inView={educationInView}
                  isLast={index === profile.education.length - 1}
                  title={edu.degree}
                  subtitle={edu.institution}
                  duration={edu.duration}
                  description={edu.description}
                  /*
                   * ── LOGO GUIDE ──────────────────────────────────────
                   * Pass `edu.logo` (a URL string) from your API/CMS.
                   *
                   * ✅ Logo present → shows the institution logo image
                   * ❌ Logo absent  → shows a styled initial badge
                   *    (first letter of the institution name)
                   * ────────────────────────────────────────────────────
                   */
                  logo={edu.logo ? getImageUrl(edu.logo.url) : null}
                />
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default About;
